import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Settings, MapPin, CheckCircle2 } from 'lucide-react';
interface SlideCampaignEngineResponsibilityProps {
  onContinue: () => void;
}
const SNAKKER_RESPONSIBILITIES = ['Reviews existing approved content', 'Identifies campaign opportunities', 'Develops the campaign approach', 'Creates short, relevant campaign assets', 'Adapts and localizes campaigns', 'Runs the Snakker activation', 'Measures engagement and behaviour', 'Implements improvements and shares insight'];
const AFFILIATE_RESPONSIBILITIES = ['Market priorities', 'Local context', 'Regulatory requirements', 'Language', 'Channel needs', 'Local calls to action'];
export function SlideCampaignEngineResponsibility({
  onContinue
}: SlideCampaignEngineResponsibilityProps) {
  const [stage, setStage] = useState(0);
  useEffect(() => {
    const timers = [300, 800, 1400, 2000].map((ms, idx) => setTimeout(() => setStage(idx + 1), ms));
    return () => timers.forEach(clearTimeout);
  }, []);
  return <div className="h-full w-full flex flex-col px-8 py-10 md:px-20 md:py-12 max-w-7xl mx-auto" data-id="element-296" data-mp="1ddh2nc134qmu">
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} className="space-y-3 shrink-0" data-id="element-297" data-mp="1rcqj8ct7dmrc">
        <p className="text-[#6C2BD9] font-semibold tracking-widest uppercase text-sm" data-id="element-298" data-mp="21awlivix8uwq">
          Solution 2 · Mature & Legacy Brands
        </p>
        <h2 className="text-3xl md:text-5xl font-serif text-slate-900" data-id="element-299" data-mp="1wbjya9o51ltm">
          We run the campaign engine
        </h2>
        <p className="text-lg text-slate-500" data-id="element-300" data-mp="2b3297t254q64">
          HQ sets the direction. Snakker runs the cycle. Affiliates contribute
          only what only they can.
        </p>
      </motion.div>

      <div className="flex-1 min-h-0 flex flex-col justify-center max-w-6xl mx-auto w-full mt-8 space-y-6" data-id="element-301" data-mp="2k1xft693k29y">
        <div className="grid grid-cols-1 md:grid-cols-[1.5fr_1fr] gap-6" data-id="element-302" data-mp="2f0hx3kue67uq">
          {/* Snakker column */}
          <motion.div initial={{
          opacity: 0,
          y: 16
        }} animate={{
          opacity: stage >= 1 ? 1 : 0,
          y: stage >= 1 ? 0 : 16
        }} className="bg-white border-2 border-[#6C2BD9]/20 rounded-2xl p-6 md:p-8 shadow-[0_0_40px_rgba(108,43,217,0.06)]" data-id="element-303" data-mp="1cnbwyoenj4sy">
            <div className="flex items-center gap-4 mb-6" data-id="element-304" data-mp="1vf4p6glwclzm">
              <div className="w-12 h-12 rounded-xl bg-[#6C2BD9]/10 text-[#6C2BD9] flex items-center justify-center shrink-0" data-id="element-305" data-mp="20e7ve4dm4y96">
                <Settings className="w-6 h-6" data-id="element-306" data-mp="25evi4dut6sxl" />
              </div>
              <h3 className="text-slate-900 font-bold text-xl" data-id="element-307" data-mp="29f5p9tuc7pe1">
                What Snakker takes responsibility for
              </h3>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4" data-id="element-308" data-mp="b1m88kycp5et">
              {SNAKKER_RESPONSIBILITIES.map((item, idx) => <motion.div key={item} initial={{
              opacity: 0,
              x: -8
            }} animate={{
              opacity: stage >= 2 ? 1 : 0,
              x: stage >= 2 ? 0 : -8
            }} transition={{
              delay: stage >= 2 ? idx * 0.05 : 0
            }} className="flex items-start gap-3" data-id="element-309" data-mp="7hlslwkvx4lb" data-mpi={idx}>
                  <CheckCircle2 className="w-5 h-5 text-[#6C2BD9] shrink-0 mt-0.5" data-id="element-310" data-mp="lhn19y7e5gbc" />
                  <span className="text-slate-700 text-sm leading-snug" data-id="element-311" data-mp="lftvsm8il24x">
                    {item}
                  </span>
                </motion.div>)}
            </div>
          </motion.div>

          {/* Affiliate column */}
          <motion.div initial={{
          opacity: 0,
          y: 16
        }} animate={{
          opacity: stage >= 2 ? 1 : 0,
          y: stage >= 2 ? 0 : 16
        }} className="bg-slate-50 border border-slate-200 rounded-2xl p-6 md:p-8" data-id="element-312" data-mp="326k5bdjkqp5n">
            <div className="flex items-center gap-4 mb-4" data-id="element-313" data-mp="tqskeh7xdilb">
              <div className="w-12 h-12 rounded-xl bg-slate-200 text-slate-600 flex items-center justify-center shrink-0" data-id="element-314" data-mp="fycpk3p6gqvh">
                <MapPin className="w-6 h-6" data-id="element-315" data-mp="k0g1sujo7zht" />
              </div>
              <h3 className="text-slate-900 font-bold text-xl" data-id="element-316" data-mp="17p8qq71vl72x">
                What the affiliate provides
              </h3>
            </div>
            <p className="text-slate-500 text-sm mb-6" data-id="element-317" data-mp="1cmsi88nl3gct">
              Deliberately light — only what only they can give.
            </p>
            <ul className="space-y-4" data-id="element-318" data-mp="yqn9cf853y3q">
              {AFFILIATE_RESPONSIBILITIES.map((item, idx) => <motion.li key={item} initial={{
              opacity: 0,
              x: 8
            }} animate={{
              opacity: stage >= 3 ? 1 : 0,
              x: stage >= 3 ? 0 : 8
            }} transition={{
              delay: stage >= 3 ? idx * 0.05 : 0
            }} className="text-slate-700 text-sm pl-2" data-id="element-319" data-mp="3jejgraqeovnd" data-mpi={idx}>
                  {item}
                </motion.li>)}
            </ul>
          </motion.div>
        </div>

        {/* Bottom banner */}
        <motion.div initial={{
        opacity: 0,
        y: 12
      }} animate={{
        opacity: stage >= 4 ? 1 : 0,
        y: stage >= 4 ? 0 : 12
      }} className="bg-[#6C2BD9]/[0.08] border border-[#6C2BD9]/20 rounded-2xl px-8 py-6 text-center" data-id="element-320" data-mp="2x75wgzp39pq3">
          <p className="text-slate-800 text-lg md:text-xl leading-relaxed" data-id="element-321" data-mp="323mo43vhym7x">
            More campaign activity on revenue-generating brands —{' '}
            <span className="text-[#6C2BD9] font-bold" data-id="element-322" data-mp="29gtesx41kemb">
              without additional internal headcount
            </span>{' '}
            and without taking hours away from strategic priorities.
          </p>
        </motion.div>
      </div>
    </div>;
}